import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Comchek from '../features/views/comchek/Comchek';
import Create from '../helpers/modal/CreateModal';
import Modal from '../helpers/modal/Modal';
import BBITable from '../helpers/bBITable/BBITable';
import Get from '../api/internal/Get';
import View from '../helpers/slab/View';
import ViewLoad from '../features/views/load/Load';
import ViewRental from '../features/views/rental/Rental';
import { FaMoneyCheckAlt, FaSearchDollar } from 'react-icons/fa';
import {
  MdCancelPresentation,
  MdDownload,
  MdOutlineRefresh,
} from 'react-icons/md';
import Filter from '../helpers/filter/Filter';
import FilterData from '../helpers/filter/FilterData';
import { ComchekBoardItem } from '../interfaces/comchek';
import { CSVLink } from 'react-csv';
import { LoadingSpinner } from '../helpers/loadingSpinner/LoadingSpinner';
import classes from '../assets/Global.module.css';
import ComchekSlab from '../features/views/comchek/ComchekSlab';

export default function ComchekBoard({ toggleBoardRefresh }) {
  const [comcheks, setComcheks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterValue, setFilterValue] = useState<string>('');

  const GetComchekBoard = useCallback(() => {
    setIsLoading(true);
    Get('/Comchek/GetComchekBoard')
      .then(res => {
        if (res) {
          setComcheks(formatComcheksData(res.data));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    GetComchekBoard();
  }, [toggleBoardRefresh, GetComchekBoard]);

  const formatComcheksData = (data: ComchekBoardItem[]) => {
    return data
      .sort((a, b) => b.comchekId - a.comchekId) // Sort descending by comchekId
      .map(comchek => ({
        ...comchek,
        loadRental: comchek.loadId
          ? comchek.loadId
          : comchek.rentalId
          ? 'Rental: ' + comchek.rentalId
          : '',
        amount: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(comchek.amount),
        fee: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(comchek.fee),
        balance: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(comchek.balance),
        issuedOn: new Date(comchek.issuedOn).toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZone: 'UTC',
        }),
      }));
  };

  const columnConfig = [
    {
      key: '1',
      attribute: 'expressCheckNumber',
      attributeprimaryhidden: 'comchekId',
      header: 'ECN',
      onClick: e =>
        View(
          <ComchekSlab
            comchekId={e.target.dataset.attributeprimaryhidden}
            expressCheckNumber={e.target.innerText}
          />,
        ),
    },
    {
      key: '2',
      attribute: 'amount',
      header: 'Amount',
    },
    {
      key: '3',
      attribute: 'fee',
      header: 'Fee',
    },
    {
      key: '4',
      attribute: 'balance',
      header: 'Balance',
    },
    {
      key: '5',
      attribute: 'status',
      header: 'Status',
    },
    {
      key: '6',
      attribute: 'statusChecked',
      header: 'Checked (UTC)',
    },
    {
      key: '7',
      attribute: 'usedDate',
      header: 'Used Date (UTC)',
    },
    {
      key: '8',
      attribute: 'usedLocation',
      header: 'Used Location',
    },
    {
      key: '9',
      attribute: 'loadRental',
      attributeprimaryhidden: 'loadId',
      attributesecondaryhidden: 'rentalId',
      header: 'Load #',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden ? (
            <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
          ) : (
            e.target.dataset.attributesecondaryhidden && (
              <ViewRental
                rentalId={e.target.dataset.attributesecondaryhidden}
              />
            )
          ),
        ),
    },
    {
      key: '10',
      attribute: 'truckName',
      header: 'Truck',
    },
    {
      key: '11',
      attribute: 'trailerName',
      header: 'Trailer',
    },
    {
      key: '12',
      attribute: 'driverName',
      header: 'Driver',
    },
    {
      key: '13',
      attribute: 'issuedOn',
      header: 'Issued On (UTC)',
    },
    {
      key: '14',
      attribute: 'createdBy',
      header: 'Created By',
    },
    {
      key: '15',
      attribute: 'reasonCode',
      header: 'Issue Type',
    },
    {
      key: '16',
      attribute: 'note',
      header: 'Note',
    },
    {
      key: '17',
      attribute: 'cancelledOn',
      header: 'Cancelled On (UTC)',
    },
    {
      key: '18',
      attribute: 'cancelledBy',
      header: 'Cancelled By',
    },
    {
      key: '19',
      attribute: 'vendorName',
      header: 'Vendor',
    },
  ];

  const CreateButton = () => {
    const handleClick = () => {
      Create(
        <Modal>
          <Comchek onSubmit={GetComchekBoard} onSuccess={GetComchekBoard} />
        </Modal>,
      );
    };
    return (
      <>
        <button onClick={handleClick}>
          <FaMoneyCheckAlt />
          Create
        </button>
      </>
    );
  };

  const CheckStatusButton = () => {
    const handleClick = () => {
      Create(
        <Modal>
          <Comchek defaultTab="status" onSuccess={GetComchekBoard} />
        </Modal>,
      );
    };
    return (
      <button onClick={handleClick}>
        <FaSearchDollar />
        Check Status
      </button>
    );
  };

  const CancelButton = () => {
    const handleClick = () => {
      Create(
        <Modal>
          <Comchek defaultTab="cancel" onSuccess={GetComchekBoard} />
        </Modal>,
      );
    };
    return (
      <button onClick={handleClick}>
        <MdCancelPresentation />
        Cancel
      </button>
    );
  };

  const DownloadCsvButton = () => {
    const csvFileName = `Comcheks_${new Date().toLocaleDateString()}.csv`;
    return (
      <button>
        <CSVLink
          data={FilterData(comcheks, filterValue)}
          headers={[
            { label: 'ECN', key: 'expressCheckNumber' },
            { label: 'Amount', key: 'amount' },
            { label: 'Fee', key: 'fee' },
            { label: 'Balance', key: 'balance' },
            { label: 'Status', key: 'status' },
            { label: 'Checked (UTC)', key: 'statusChecked' },
            { label: 'Used Date (UTC)', key: 'usedDate' },
            { label: 'Used Location', key: 'usedLocation' },
            { label: 'Load #', key: 'loadRental' },
            { label: 'Asset Name', key: 'assetName' },
            { label: 'Driver', key: 'driverName' },
            { label: 'Issued On (UTC)', key: 'issuedOn' },
            { label: 'Created By', key: 'createdBy' },
            { label: 'Issue Type', key: 'reasonCode' },
            { label: 'Note', key: 'note' },
            { label: 'Cancelled On (UTC)', key: 'cancelledOn' },
            { label: 'Cancelled By', key: 'cancelledBy' },
            { label: 'Vendor', key: 'vendorName' },
          ]}
          filename={csvFileName}
        >
          <MdDownload />
          Download
        </CSVLink>
      </button>
    );
  };

  return (
    <>
      <h1>Comcheks</h1>
      <Header>
        <div>
          <CreateButton />
          <CheckStatusButton />
          <CancelButton />
          <button onClick={GetComchekBoard}>
            <MdOutlineRefresh />
            <span>Refresh Board</span>
          </button>
          <DownloadCsvButton />
        </div>
        <Filter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          placeholder="Filter Comcheks"
        />
      </Header>
      {isLoading ? (
        <div className={classes.spinnerContainer}>
          <LoadingSpinner />
        </div>
      ) : (
        <BBITable
          data={FilterData(comcheks, filterValue)}
          columns={columnConfig}
          compactView={false}
        />
      )}
    </>
  );
}
